import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { asId, normalizeConversation } from '../messages/travelChatModel';
import { CHAT_DISABLED_MESSAGE, imErrorCode, imErrorKey, isAuthExpiredError } from '../messages/travelDirectError';

export const SHARE_MESSAGE_MAX = 2000;

const SHAREABLE_CONVERSATION_TYPES = new Set(['DIRECT', 'GROUP']);
const SNOWFLAKE_PATTERN = /^[1-9]\d*$/;

/** 公開行程僅在 PUBLIC 可分享；後端仍會在送出時重新驗證。 */
export function canOfferJourneyImShare({ isAuthenticated, journey }) {
  return Boolean(isAuthenticated && journey && journey.visibility === 'PUBLIC' && journey.publicationId);
}

/** 只保留可寫入的既有 DIRECT／GROUP 對話；客服與旅程業務群組不可作為分享目標。
 * status 必須明確為 ACTIVE（與 IM MessageService.requireActiveConversation 對齊）。
 * 缺漏／null status 不得視為可分享（會導致 FE 顯示後後端 409）。
 */
export function shareableConversations(list) {
  return (Array.isArray(list) ? list : [])
    .map(normalizeConversation)
    .filter((row) => SNOWFLAKE_PATTERN.test(row.conversationId))
    .filter((row) => SHAREABLE_CONVERSATION_TYPES.has(String(row.conversationType || '').toUpperCase()))
    .filter((row) => String(row.status || '').toUpperCase() === 'ACTIVE');
}

/** 成功回應的 conversationId 必須是合法 ID，否則退回使用者選取的對話。 */
export function shareResultConversationId(result, fallbackConversationId) {
  const id = asId(result?.conversationId);
  return SNOWFLAKE_PATTERN.test(id) ? id : asId(fallbackConversationId);
}

/** 分享失敗一律轉為安全文案，不顯示後端代碼或原始錯誤訊息。 */
export function shareErrorMessage(cause, t) {
  const key = imErrorKey(cause);
  const code = imErrorCode(cause);
  if (isAuthExpiredError(cause)) return '登入已過期，請重新登入後再試。';
  if (key === 'im.chat.disabled' || code === 91148) return CHAT_DISABLED_MESSAGE;
  if (key === 'travel.journey.share.not.available' || cause?.httpStatus === 404) {
    return '這個行程目前無法分享。';
  }
  if (key === 'im.blocked' || code === 91080) return '目前無法分享到這個聊天室。';
  if (key === 'im.conversation.not_writable' || code === 91149) return '這個聊天室目前為唯讀，無法分享。';
  if (key === 'permissions.insufficient.error' || code === 90007) return '你目前無法在這個聊天室分享內容。';
  if (key === 'travel.journey.share.im.unavailable' || cause?.httpStatus >= 500 || (!cause?.raw && cause?.networkCode)) {
    return '聊天室暫時無法連線，請稍後再試。';
  }
  return resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed');
}
